<template>
  <div class="z-[9999] relative">
    <Dialog :title="$t('Set new password')"
     :custom-max-width-class="'max-w-md'"
     :close-on-backdrop-click="false"
     v-model="password_forgot_dialog_visible">
      <div class="px-4 py-4">
          <div v-if="isSuccess" class="text-center">
            <div class="mb-4">
              <svg class="mx-auto h-12 w-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
              </svg>
              <h3 class="mt-2 text-lg font-medium text-neutral-900">{{ $t('Password successfully reset!') }}</h3>
              <p class="mt-1 text-sm text-neutral-600">{{ $t('You are now logged in and can continue using the app.') }}</p>
            </div>
            <button
              type="button"
              @click.prevent="continueToApp"
              class="btn-main w-full flex items-center justify-center min-h-[48px]"
            >
              {{ $t('Continue') }}
            </button>
          </div>
          <form
            v-else
            id="forgot_password"
            class="form"
            ref="forgotpasswordform"
            @submit.prevent
          >
            <div class="mt-4">
              <label for="user_password" class="label">{{ $t('New password') }}</label>
              <input
                autocomplete="new-password"
                required="required"
                type="password"
                value=""
                name="user[password]"
                id="user_password"
                class="input"
                :placeholder="$t('Enter new password')"
                :disabled="isSubmitting"
                v-model="password"
              />
            </div>
            <div class="mt-4">
              <label for="user_password_confirmation" class="label">{{ $t('Confirm password') }}</label>
              <input
                autocomplete="new-password"
                required="required"
                type="password"
                value=""
                name="user[password_confirmation]"
                id="user_password_confirmation"
                class="input"
                :placeholder="$t('Confirm new password')"
                :disabled="isSubmitting"
                v-model="passwordConfirmation"
              />
              <p v-if="passwordError" class="mt-1 text-sm text-red-600">{{ passwordError }}</p>
            </div>
            <input
              type="hidden"
              name="user[email]"
              :value=this.userEmail
            />
            <div class="mt-4 flex justify-center">
              <button
                type="button"
                @click.prevent="forgotPassword"
                class="btn-main w-full flex items-center justify-center gap-2 min-h-[48px]"
                :disabled="isSubmitting"
              >
                <svg v-if="isSubmitting" class="spinner" stroke="currentColor" fill="none" stroke-width="2" viewBox="0 0 24 24" stroke-linecap="round" stroke-linejoin="round" height="20px" width="20px" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 6l0 -3"></path>
                  <path d="M16.25 7.75l2.15 -2.15"></path>
                  <path d="M18 12l3 0"></path>
                  <path d="M16.25 16.25l2.15 2.15"></path>
                  <path d="M12 18l0 3"></path>
                  <path d="M7.75 16.25l-2.15 2.15"></path>
                  <path d="M6 12l-3 0"></path>
                  <path d="M7.75 7.75l-2.15 -2.15"></path>
                </svg>
                {{ isSubmitting ? $t('Setting password...') : $t('Set password') }}
              </button>
            </div>
          </form>
      </div>
    </Dialog>
  </div>
</template>

<script>
import Dialog from "./dialog";

export default {
  props: [
    "button-class",
    "time-now",
    "email",
  ],
  data() {
    return {
      password: '',
      passwordConfirmation: '',
      passwordError: '',
      login_error_msg: "",
      password_forgot_dialog_visible: true,
      userEmail: '',
      isSubmitting: false,
      isSuccess: false,
      redirectUrl: '',
    };
  },
  mounted() {
    this.userEmail = this.email ? this.email : '';
  },
  components: {
    Dialog,
  },

  methods: {
    validatePasswords() {
      if (!this.password) {
        this.passwordError = 'Password is required';
        return false;
      }
      if (!this.passwordConfirmation) {
        this.passwordError = 'Please confirm your password';
        return false;
      }
      if (this.password !== this.passwordConfirmation) {
        this.passwordError = 'Passwords do not match';
        return false;
      }
      this.passwordError = '';
      return true;
    },

    async forgotPassword() {
      if (!this.validatePasswords()) {
        return;
      }

      this.isSubmitting = true;
      const data = new FormData(this.$refs.forgotpasswordform);
      
      try {
        const response = await fetch('/users/forgot/password', {
          method: 'POST',
          body: data,
          headers: {
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
            'Accept': 'application/json'
          },
          credentials: 'same-origin'
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to reset password');
        }
        
        const responseData = await response.json();
        this.redirectUrl = responseData.redirect_url || '/';
        this.isSuccess = true;
      } catch (error) {
        this.login_error_msg = error.message || 'An error occurred while resetting your password';
        console.error('Password reset error:', error);
      } finally {
        this.isSubmitting = false;
      }
    },

    continueToApp() {
      window.location.href = this.redirectUrl;
    }
  },
  watch: {
    password_forgot_dialog_visible (newVal) {
      if (!newVal && window.location.search.includes("view_popup")) {
        const newUrl = window.location.pathname + window.location.search.replace(/[?&]view_popup=[^&]*(&|$)/g, '');
        window.history.replaceState({}, '', newUrl);
      }
    }
  }
};
</script>

<style scoped>
.btn-soc {
  @apply p-2 shadow-md rounded-lg border border-neutral-100 transition duration-150 ease-in-out;
}

.btn-soc:hover {
  @apply shadow-lg;
}

.spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
